import { notification } from 'antd';
import CardTile from 'common/cardtile/component/card-tile';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Icon } from 'ui';
import AnnouncementsFeed from '../../common/announcements-feed/components/announcements-feed';
import KpiIndicator from '../../common/home-tiles/components/kpi-indicator';
import PlannerGroupToggle from '../../header/planner-group-toggle/components/planner-group-toggle';
import { capitalizeFirstLetter, isObjectNullOrEmpty } from '../../utils/helpers';
import TileWrapper from './tile-wrapper';
// eslint-disable-next-line max-len
import AnnouncementsFeedTitle from '../../common/announcements-feed/components/announcements-feed-title';
// eslint-disable-next-line max-len
import campaignHeaderImage from '../../../assets/images/campaign_header.png';
// eslint-disable-next-line max-len
import AnnouncementsFeedSidepanel from '../../common/announcements-feed/components/announcements-sidepanel';
import ErrorHandler from '../../common/error/components/error-handler';
import CampaignTile from '../../settings/components/campaigns/campaign-home-tile';
import {
  announcementBannerHeight,
  focusCampaignKeySeparator,
  focusCampaignPrefix,
  homeLayout,
} from '../../utils/app-configs';
import { resetCampaignSuccess } from '../containers/landing-zone/landing-zone-actions';

const ReactGridLayout = styled(WidthProvider(Responsive))`
  height: ${p => p.height};
`;

const StyledIcon = styled(Icon)`
  color: white;
  margin-right: 10px;
`;

const Home = ({
  intl,
  getKpisForLandingZone,
  getCampaignsForLandingZone,
  kpis,
  campaigns = [],
}) => {
  const dispatch = useDispatch();
  const [breakPoint, setBreakPoint] = useState('lg');
  const [notificationApi, contextHolder] = notification.useNotification();
  const [isAnnouncementsSidepanelOpen, setIsAnnouncementsSidepanelOpen] = useState(false);
  const { hasAnnouncementBanner, plannerGroups } = useSelector(state => state.user);
  const {
    isLoadingKpisLandingZone,
    fetchCampaignsLandingZoneSuccess,
    error,
  } = useSelector(state => state.landingZone);
  const {
    isLoadingAnnouncements,
  } = useSelector(state => state.announcements);
  const preferredPlannerGroup = JSON.parse(
    localStorage.getItem('preferredPlannerGroupByServiceCenterId') || '{}',
  ) || {};
  const layouts = localStorage.getItem('homeLayout') ?
    JSON.parse(localStorage.getItem('homeLayout')) :
    homeLayout;

  const updateLocalStorage = (data) => {
    localStorage.setItem('homeLayout', data);
  };

  useEffect(() => {
    const width = window.innerWidth;
    if (width > 520 || width < 520) {
      setBreakPoint('xs');
    }
    if (width > 808) {
      setBreakPoint('sm');
    }
    if (width > 1036) {
      setBreakPoint('md');
    }
    if (width > 1240) {
      setBreakPoint('lg');
    }
    const serviceCenterId = JSON.parse(localStorage.getItem('serviceCenter'))?.value || '';
    if (preferredPlannerGroup[serviceCenterId]) {
      const foundPlannerGroups =
        plannerGroups.filter(p =>
          p.number === preferredPlannerGroup[serviceCenterId] ||
          p.id === preferredPlannerGroup[serviceCenterId],
        );
      if (foundPlannerGroups.length !== 1) {
        notificationApi.warning({
          duration: 0,
          placement: 'top',
          key: 'reselectPlannerGroup',
          message: intl.formatMessage({
            id: 'home.PLANNER_GROUP_RESELECT',
          }),
          description: <PlannerGroupToggle
            closeParent={() => {
              notificationApi.destroy('reselectPlannerGroup');
            }}
            extraStyles={{
              minWidth: '200px',
            }}
            popupContainer={() => document.body}
          />,
        });
      } else {
        const localData = JSON.parse(
          localStorage.getItem('preferredPlannerGroupByServiceCenterId') ||
          '{}',
        );
        if (serviceCenterId && foundPlannerGroups[0]?.id) {
          localStorage.setItem('preferredPlannerGroupByServiceCenterId', JSON.stringify({
            ...localData,
            [serviceCenterId]: foundPlannerGroups[0]?.id,
          }));
        }
      }
    }
    getKpisForLandingZone();
    getCampaignsForLandingZone({
      plannerGroup: preferredPlannerGroup[serviceCenterId] || plannerGroups[0]?.id,
    });
  }, []);

  // Check for all dynamic tiles if they are loaded in, if so => clean up localStorage
  // useEffect needed for each dynamic tile
  useEffect(() => {
    if (fetchCampaignsLandingZoneSuccess) {
      const updatedLayouts = localStorage.getItem('homeLayout') ?
        JSON.parse(localStorage.getItem('homeLayout')) :
        homeLayout;
      Object.keys(updatedLayouts).forEach((k) => {
        updatedLayouts[k] = updatedLayouts[k].filter(l =>
          !l.i.includes(`${focusCampaignPrefix}${focusCampaignKeySeparator}`) ||
          campaigns
            ?.filter(c => c.runningCampaign?.locations?.includes('homepage'))
            ?.find(c =>
              l.i === `${focusCampaignPrefix}${focusCampaignKeySeparator}${c.runningCampaign?.id}`,
            ),
        )
          ?.filter(l => homeLayout[k].find(b => l.i.includes(b.i)))
          ?.map(s => ({
            ...s,
            isResizable: homeLayout[k].find(f => f.i === s.i)?.isResizable ?? s.isResizable,
            isDraggable: homeLayout[k].find(f => f.i === s.i)?.isDraggable ?? s.isDraggable,
            static: homeLayout[k].find(f => f.i === s.i)?.static ?? s.static,
          }));
      });
      updateLocalStorage(JSON.stringify(updatedLayouts));
      dispatch(resetCampaignSuccess());
    }
  }, [fetchCampaignsLandingZoneSuccess]);

  const handleLayoutChange = (allLayouts, isUpdate) => {
    const updatedLayouts = localStorage.getItem('homeLayout') ?
      JSON.parse(localStorage.getItem('homeLayout')) :
      homeLayout;

    // Update
    if (isUpdate) {
      updatedLayouts[breakPoint] = updatedLayouts[breakPoint]?.map(l =>
        allLayouts.find(a => a.i === l.i) || l,
      );
    }

    // Add
    const missingLayouts = allLayouts?.filter(a =>
      !updatedLayouts[breakPoint].find(l => l.i === a.i),
    );
    if (missingLayouts?.length) {
      Object.keys(updatedLayouts).forEach((k) => {
        updatedLayouts[k] = [
          ...updatedLayouts[k],
          ...missingLayouts.map((l) => {
            const defaultValues =
              updatedLayouts[k].find(u => u.i === l.i.split(focusCampaignKeySeparator)[0]);
            if (defaultValues) {
              return {
                ...defaultValues,
                i: l.i,
                x: l.x,
                y: l.y,
              };
            }
            return l;
          }),
        ];
      });
    }
    updateLocalStorage(JSON.stringify(updatedLayouts));
  };

  const defGridHeight = 110;
  const gridHeight = hasAnnouncementBanner ?
    defGridHeight + announcementBannerHeight :
    defGridHeight;

  const maxElement = layouts[breakPoint]?.reduce((max, c) =>
    c.y >= max.y ? c : max, layouts[breakPoint][0],
  );

  const getDataGrid = key => layouts[breakPoint].find(l => l.i === key) ||
    homeLayout[breakPoint].find(l => l.i === key);

  return (
    <>
      {contextHolder}
      <ErrorHandler type='notification' error={error} />
      <ReactGridLayout
        className='layout'
        // If we want to allow do user to drop wherever they want, we shoud just take a fix height
        // in px, that is big enough. That way thay can drop wherever they want.
        height={isObjectNullOrEmpty(maxElement) ?
          `calc(100vh - ${gridHeight}px)` :
          `max(calc(100vh - ${gridHeight}px), ${(maxElement?.y + maxElement?.h) * 40}px)`}
        rowHeight={30}
        resizeHandles={['se']}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
        cols={{ lg: 24, md: 12, sm: 6, xs: 4 }}
        isBounded={true}
        draggableHandle='.draggable_handle'
        onLayoutChange={e => handleLayoutChange(e, false)}
        autoSize={false}
        onBreakpointChange={setBreakPoint}
        onDragStop={e => handleLayoutChange(e, true)}
        onResizeStop={e => handleLayoutChange(e, true)}
      >
        <TileWrapper
          key='announcements'
          data-grid={getDataGrid('announcements')}
        >
          <CardTile
            data-test-id='cops-announcements'
            title={
              <AnnouncementsFeedTitle
                setIsSidepanelOpen={setIsAnnouncementsSidepanelOpen}
              />
            }
            canScrollY={false}
            description={<AnnouncementsFeed isHome={true} />}
            isDraggable={
              layouts[breakPoint]?.find(k => k.i === 'announcements')?.isDraggable
            }
            isLoading={isLoadingAnnouncements}
          />
        </TileWrapper>
        <TileWrapper
          key='contextualInfo'
          data-grid={getDataGrid('contextualInfo')}
        >
          <CardTile
            data-test-id='cops-contextualInfo'
            title={capitalizeFirstLetter(intl.formatMessage({ id: 'common.titles.INFO' }))}
            description={
              <KpiIndicator data={kpis} />
            }
            isLoading={isLoadingKpisLandingZone}
          />
        </TileWrapper>
        {campaigns
          ?.filter(c => c.runningCampaign?.locations?.includes('homepage'))
          ?.map((c) => {
            const k = `${focusCampaignPrefix}${focusCampaignKeySeparator}${c.runningCampaign?.id}`;
            return <TileWrapper
              key={k}
              data-grid={
                getDataGrid(k) ||
                getDataGrid('focusCampaign')
              }
            >
              <CardTile
                data-test-id='cops-campaign'
                title={c.runningCampaign?.title}
                renderHeaderIcon={() => <StyledIcon type='bullseye-pointer' solid={true} />}
                description={
                  <CampaignTile
                    {...c}
                    breakPoint={breakPoint}
                  />}
                isDraggable={true}
                headerTextColor='white'
                headerBackgroundColor='#0098BC'
                headerIconColor='white'
                headerBackgroundImage={`url(${campaignHeaderImage})`}
              />
            </TileWrapper>;
          })}
      </ReactGridLayout>
      {isAnnouncementsSidepanelOpen &&
        <AnnouncementsFeedSidepanel
          setIsSidepanelOpen={setIsAnnouncementsSidepanelOpen}
        />
      }
    </>
  );
};

Home.propTypes = {
  intl: PropTypes.object,
  kpis: PropTypes.array,
  getKpisForLandingZone: PropTypes.func,
  getCampaignsForLandingZone: PropTypes.func,
  campaigns: PropTypes.array,
};

export default injectIntl(Home);
